<template>
  <form
    class="form form--custom"
    novalidate
    @submit.prevent="handleSubmit"
  >
    <div
      class="pa-4"
      :style="`height: ${contentHeight - (isLoading ? 0 : 83)}px; overflow: auto;`"
    >
      <Loader v-if="isLoading" />

      <EmptyState v-if="isSuccess && !companiesLength" />

      <div v-if="isSuccess && companiesLength">
        <label>{{ $t(`${namespace}.form.fieldCompanyLabel`) }}</label>
        <div class="autocomplete__wrapper">
          <transition name="fade">
            <AutoComplete
              v-if="autocompleteCompany.visible"
              kind="webCallCompany"
              :data="autocompleteCompany.value"
              :placeholder="$t(`${namespace}.form.fieldCompanyPlaceholder`)"
              :loading="autocompleteCompany.loading"
              :disabled="autocompleteCompany.disabled"
              :isValid="autocompleteCompany.valid"
              requestOnClick
              required
              @input="handleSetCompanyData"
            />
          </transition>
        </div>

        <div v-if="formFieldsWrapper" :class="{ 'fields--disabled': !Boolean(autocompleteCompany.value) }">
          <label>{{ $t(`${namespace}.form.fieldEmployeeLabel`) }}</label>
          <div class="autocomplete__wrapper">
            <transition name="fade">
              <AutoComplete
                v-if="autocompleteEmployee.visible"
                kind="webCallEmployee"
                :placeholder="$t(`${namespace}.form.fieldEmployeePlaceholder`)"
                :companyId="companyId"
                :loading="autocompleteCompany.loading"
                :isValid="autocompleteEmployee.valid"
                required
                @input="handleSetEmployeeData"
              />
            </transition>
          </div>

          <div class="locations__wrapper">
            <div class="locations__wrapper__item">
              <LocationAutocomplete
                kind="origin"
                :model-value="locations.origin"
                :label="$t(`${namespace}.form.fieldOriginLabel`)"
                :placeholder="$t(`${namespace}.form.fieldOriginPlaceholder`)"
                :name="$t(`${namespace}.form.fieldOriginName`)"
                favoriteAddress
                v-validate="{ required: true }"
                :data-vv-name="$t(`${namespace}.form.fieldOriginName`)"
                :error-messages="errors.collect($t(`${namespace}.form.fieldOriginName`))"
                @update:modelValue="(val) => handleLocationAutocompleteUpdate(val, 'origin', $t(`${namespace}.form.fieldOriginName`))"
                @hideMapOverlay="() => {
                  hideMapOverlay();
                  setPositions()
                }"
                @getFavoriteAddress="openFavoriteAddressDialog"
              />
            </div>

            <div class="locations__wrapper__item">
              <LocationAutocomplete
                kind="destiny"
                :model-value="locations.destiny"
                :label="$t(`${namespace}.form.fieldDestinyLabel`)"
                :placeholder="$t(`${namespace}.form.fieldDestinyPlaceholder`)"
                :name="$t(`${namespace}.form.fieldDestinyName`)"
                favoriteAddress
                v-validate="{ required: true }"
                :data-vv-name="$t(`${namespace}.form.fieldDestinyName`)"
                :error-messages="errors.collect($t(`${namespace}.form.fieldDestinyName`))"
                @update:modelValue="(val) => handleLocationAutocompleteUpdate(val, 'destiny', $t(`${namespace}.form.fieldDestinyName`))"
                @hideMapOverlay="() => {
                  hideMapOverlay();
                  setPositions()
                }"
                @getFavoriteAddress="openFavoriteAddressDialog"
              />
            </div>
          </div>

          <!--
            locations.origin
            locations.destiny

            <div v-if="employee.categoryIds && employee.categoryIds.length >= 1">
            <label>{{ $t(`${namespace}.fieldCategoriesLabel`) }}</label>
          -->

          <RidesCategories
            v-if="showRidesCategories"

            :model-value="rideCategory"
            :label="$t(`${namespace}.ridesCategories.label`)"
            :name="$t(`${namespace}.ridesCategories.name`)"
            v-validate="{ required: true }"
            :data-vv-name="$t(`${namespace}.ridesCategories.name`)"
            :error-messages="errors.collect($t(`${namespace}.ridesCategories.name`))"

            :employeeId="employeeId"
            :fromCoordinates="fromCoordinates"
            :toCoordinates="toCoordinates"

            @update:modelValue="(val) => handleRidesCategoryUpdate(val, $t(`${namespace}.ridesCategories.name`))"
            @openDelivery99Dialog="openDelivery99Dialog"
            @resetDelivery99Values="resetDelivery99Values"
          />
        </div>
      </div>
    </div>

    <footer v-if="isSuccess && showSubmitButton" class="form__footer">
      <v-btn
        type="submit"
        color="accent"
        depressed
        round
      >
        {{ $t(`${namespace}.callACar`) }}
      </v-btn>
    </footer>

    <WebCallDeliveryDialog
      :visible="delivery99Dialog"
      :delivery99Type="delivery99Type"
      @close="closeDelivery99Dialog"
    />

    <WebCallBillingDataDialog
      :data="form"
      :visible="webCallBillingDataDialog"
      :employeeId="employeeId"
      :companyId="companyId"
      @close="closeWebCallBillingDataDialog"
      @updateRidesList="updateRidesList"
    />

    <FavoriteAddressDialog
      :visible="favoriteAddressDialog.visibility"
      :kind="favoriteAddressDialog.ref"
      @close="closeFavoriteAddressDialog"
    />
  </form>
</template>

<script>
import { mapState } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { isObject, calcContentHeight, getRideCategoryByAttr } from 'utils/functions'
import { Omega, OmegaEventIds, OmegaBusiness, OmegaStatus } from 'common/omega'
import Loader from 'components/newWebCall/drawer/Loader'
import EmptyState from 'components/newWebCall/drawer/EmptyState'
import AutoComplete from 'components/AutoComplete'
import LocationAutocomplete from 'components/newWebCall/drawer/LocationAutocomplete'
import RidesCategories from 'components/newWebCall/drawer/RidesCategories'
import WebCallDeliveryDialog from 'components/newWebCall/drawer/WebCallDeliveryDialog'
import WebCallBillingDataDialog from 'components/newWebCall/drawer/WebCallBillingDataDialog'
import FavoriteAddressDialog from 'components/newWebCall/drawer/FavoriteAddressDialog'

export default {
  name: 'Form',
  components: {
    Loader,
    EmptyState,
    AutoComplete,
    LocationAutocomplete,
    RidesCategories,
    WebCallDeliveryDialog,
    WebCallBillingDataDialog,
    FavoriteAddressDialog
  },
  computed: {
    ...mapState({
      webcallMoveMarkersToggle: ({ webcallMoveMarkers }) => webcallMoveMarkers.toggle
    }),
    isLoading () {
      return this.currentStatus === STATUS_IS_LOADING
    },
    isSuccess () {
      return this.currentStatus === STATUS_SUCCESS
    },
    isFailed () {
      return this.currentStatus === STATUS_FAILED
    },
    companyId () {
      return isObject(this.autocompleteCompany) && isObject(this.autocompleteCompany.value) && this.autocompleteCompany.value.id
        ? this.autocompleteCompany.value.id
        : null
    },
    employeeId () {
      return isObject(this.autocompleteEmployee) && isObject(this.autocompleteEmployee.value) && this.autocompleteEmployee.value.id
        ? this.autocompleteEmployee.value.id
        : null
    },
    showRidesCategories () {
      return Boolean(
        this.employeeId &&
        this.fromCoordinates && this.fromCoordinates.latitude &&
        this.toCoordinates && this.toCoordinates.latitude
      )
    },
    showSubmitButton () {
      return this.companiesLength
    }
  },
  data () {
    return {
      namespace: 'Views.NewWebCallPage',
      contentHeight: null,
      currentStatus: null,
      formFieldsWrapper: true,
      companiesLength: 0,
      autocompleteCompany: {
        visible: false,
        loading: false,
        value: null,
        disabled: false,
        valid: true
      },
      autocompleteEmployee: {
        visible: true,
        loading: false,
        value: null,
        valid: true
      },
      locations: {
        origin: null,
        destiny: null
      },
      rideCategory: null,
      fromCoordinates: null,
      toCoordinates: null,
      delivery99Dialog: false,
      webCallBillingDataDialog: false,
      form: {},
      receiver: {},
      favoriteAddressDialog: {
        visibility: false,
        ref: null
      },
      delivery99Type: 'delivery99'
    }
  },
  methods: {
    calcContentHeight () {
      this.contentHeight = calcContentHeight()
    },

    isValidObject (val) {
      return isObject(val)
    },

    handleLocationAutocompleteUpdate (val, type, name) {
      this.locations[type] = val
      this.setLocationValue(type, val)
      this.forceFormFieldValidation(name, val)
    },

    handleRidesCategoryUpdate (val, name) {
      this.rideCategory = val
      this.forceFormFieldValidation(name, val)
    },

    forceFormFieldValidation (name, val) {
      this.$validator.validate(name, val)
    },

    setLocationValue (type, val) {
      this.locations[type] = val
    },

    async getWebCallCompanies () {
      this.handleAutocompleteCompanyToggleLoading(true)

      try {
        const { data: res } = await this.$apiService.getWebCallCompanies()
        const json = Array.isArray(res.data) ? res.data : []
        const companies = json.filter(company => company.status.toLowerCase() === 'enabled')
        this.autocompleteCompany.disabled = Array.isArray(companies) && companies.length === 1
        this.companiesLength = companies.length

        if (this.autocompleteCompany.disabled) {
          this.handleSetCompanyData(companies[0])
        }

        this.handleAutocompleteCompanyToggleVisibility(true)
        this.handleAutocompleteCompanyToggleLoading(false)
      } catch (error) {
        console.log('ERROR:', error)
        this.handleAutocompleteCompanyToggleVisibility(true)
        this.handleAutocompleteCompanyToggleLoading(false)
        return error
      }
    },

    async getWebCallCompaniesLength () {
      this.currentStatus = STATUS_IS_LOADING

      try {
        await this.getWebCallCompanies()
        this.currentStatus = STATUS_SUCCESS
      } catch (error) {
        console.log('ERROR:', error)
        this.currentStatus = STATUS_FAILED
      }
    },

    handleAutocompleteCompanyToggleVisibility (boolean) {
      this.autocompleteCompany.visible = boolean
    },

    handleAutocompleteCompanyToggleLoading (boolean) {
      this.autocompleteCompany.loading = boolean
    },

    handleSetCompanyData (company) {
      this.autocompleteCompany.value = company
      this.autocompleteCompany.valid = this.isValidObject(company)
    },

    handleSetEmployeeData (employee) {
      // webcall抽屉基本信息模块操作
      Omega.trackBusiness(
        OmegaEventIds.tech_b2bv3_webcall,
        {
          business: OmegaBusiness.webcall,
          type: 'baseInfo',
          status: OmegaStatus.op
        }
      )
      this.autocompleteEmployee.value = employee
      this.autocompleteEmployee.valid = this.isValidObject(employee)
    },

    async handleSubmit () {
      try {
        // webcall叫车按钮点击操作
        Omega.trackBusiness(
          OmegaEventIds.tech_b2bv3_webcall,
          {
            business: OmegaBusiness.webcall,
            type: 'callCar',
            status: OmegaStatus.op
          }
        )
        const isValid = await this.$validator.validateAll()
        const isValidCompany = this.companyValidation()
        const isValidEmployee = this.employeeValidation()

        if (isValid && isValidCompany && isValidEmployee) {
          this.setForm()
          this.openWebCallBillingDataDialog()
        }
      } catch (error) {
        console.log('ERROR:', error)
      }
    },

    setForm () {
      const { rideCategory, companyId, employeeId, fromCoordinates, toCoordinates, autocompleteEmployee } = this
      const phoneNumber = autocompleteEmployee && autocompleteEmployee.value && autocompleteEmployee.value.phone
        ? autocompleteEmployee.value.phone
        : null
      const receiver = this.getReceiverData(rideCategory)

      this.form = {
        categoryID: rideCategory,
        from: { ...fromCoordinates },
        to: { ...toCoordinates },
        companyID: companyId,
        employeeID: employeeId,
        phoneNumber,
        addressReference: null,
        optionals: [],
        categoryIds: [],
        ...receiver
      }
    },

    getRideCategoryBizType (value = '') {
      return getRideCategoryByAttr(this, { key: 'value', value: value }).bizType
    },
    getReceiverData (rideCategory) { // 获取收件人，只在闪送时有
      return ['delivery'].includes(this.getRideCategoryBizType(rideCategory)) && this.isValidObject(this.receiver)
        ? { receiver: { ...this.receiver } }
        : {}
    },

    companyValidation () {
      this.autocompleteCompany.valid = this.isValidObject(this.autocompleteCompany.value)
      return this.isValidObject(this.autocompleteCompany.value)
    },

    employeeValidation () {
      this.autocompleteEmployee.valid = this.isValidObject(this.autocompleteEmployee.value)
      return this.isValidObject(this.autocompleteEmployee.value)
    },

    hideMapOverlay () {
      this.$emit('hideMapOverlay')
    },

    setPositions () {
      const { originPosition, destinyPosition } = this.$gmaps.getPositions()

      if (originPosition && originPosition.address) {
        this.handleLocationAutocompleteUpdate(
          originPosition.address,
          'origin',
          this.$t(`${this.namespace}.form.fieldOriginName`)
        )
      }

      if (destinyPosition && destinyPosition.address) {
        this.handleLocationAutocompleteUpdate(
          destinyPosition.address,
          'destiny',
          this.$t(`${this.namespace}.form.fieldDestinyName`)
        )
      }

      // from
      const fromCoordinatesLatitude = this.isValidObject(originPosition) && originPosition.latitude
        ? originPosition.latitude
        : null
      const fromCoordinatesLongitude = this.isValidObject(originPosition) && originPosition.longitude
        ? originPosition.longitude
        : null

      const fromCoordinatesStreet = this.isValidObject(originPosition) && originPosition.address
        ? originPosition.address
        : null
      const fromCoordinatesNumber = this.isValidObject(originPosition) && originPosition.address
        ? originPosition.number
        : null

      // to
      const toCoordinatesLatitude = this.isValidObject(destinyPosition) && destinyPosition.latitude
        ? destinyPosition.latitude
        : null
      const toCoordinatesLongitude = this.isValidObject(destinyPosition) && destinyPosition.longitude
        ? destinyPosition.longitude
        : null
      const toCoordinatesStreet = this.isValidObject(destinyPosition) && destinyPosition.address
        ? destinyPosition.address
        : null
      const toCoordinatesNumber = this.isValidObject(destinyPosition) && destinyPosition.address
        ? destinyPosition.number
        : null

      this.fromCoordinates = {
        latitude: fromCoordinatesLatitude,
        longitude: fromCoordinatesLongitude,
        street: fromCoordinatesStreet,
        number: fromCoordinatesNumber
      }

      this.toCoordinates = {
        latitude: toCoordinatesLatitude,
        longitude: toCoordinatesLongitude,
        street: toCoordinatesStreet,
        number: toCoordinatesNumber
      }
    },

    openDelivery99Dialog (val) {
      this.delivery99Type = val
      this.delivery99Dialog = true
    },

    closeDelivery99Dialog (data = null) {
      // webcall闪送品类弹窗操作
      Omega.trackBusiness(
        OmegaEventIds.tech_b2bv3_webcall,
        {
          business: OmegaBusiness.webcall,
          type: 'entregaInfo',
          status: OmegaStatus.op
        }
      )
      this.delivery99Dialog = false
      if (data) return this.setDeliveryCategoryData(data)
      this.resetDelivery99Values()
    },

    setDeliveryCategoryData ({ recipientsName = '', recipientsPhone }) {
      const phone = recipientsPhone.replace(/\D/g, '')
      this.receiver = {
        name: recipientsName,
        phone
      }
    },

    resetDelivery99Values (val = null) {
      // TODO: reset Delivery99 values
      this.rideCategory = val
    },

    closeWebCallBillingDataDialog () {
      // webcall账单信息模块操作
      Omega.trackBusiness(
        OmegaEventIds.tech_b2bv3_webcall,
        {
          business: OmegaBusiness.webcall,
          type: 'billingInfo',
          status: OmegaStatus.ex
        }
      )
      this.webCallBillingDataDialog = false
    },

    openWebCallBillingDataDialog () {
      this.webCallBillingDataDialog = true
    },

    updateRidesList () {
      this.resetFields()
      // webcall成功下单
      Omega.trackBusiness(
        OmegaEventIds.tech_b2bv3_webcall,
        {
          business: OmegaBusiness.webcall,
          type: 'all',
          status: OmegaStatus.success
        }
      )
      this.$emit('updateRidesList')
    },

    resetFields () {
      this.$emit('resetMap')

      this.formFieldsWrapper = false
      this.autocompleteEmployee = {
        visible: true,
        loading: false,
        value: null,
        valid: true
      }

      this.locations = {
        origin: null,
        destiny: null
      }

      this.rideCategory = null
      this.fromCoordinates = null
      this.toCoordinates = null
      this.form = {}
      this.receiver = {}

      setTimeout(() => { this.formFieldsWrapper = true }, 1000)
    },

    openFavoriteAddressDialog (ref) {
      this.favoriteAddressDialog = {
        visibility: true,
        ref
      }
    },

    closeFavoriteAddressDialog (obj) {
      this.favoriteAddressDialog = {
        visibility: false,
        ref: null
      }

      if (obj) {
        const { ref, address, number, latitude, longitude } = obj
        this.hideMapOverlay()
        this.$gmaps.renderFavoriteAddress({ ref, address, number, latitude, longitude })
        this.$gmaps.directions()
      }
    }
  },
  watch: {
    webcallMoveMarkersToggle () {
      this.setPositions()
    }
  },
  mounted () {
    this.calcContentHeight()
    window.addEventListener('resize', this.calcContentHeight)
    this.getWebCallCompaniesLength()
  },
  beforeDestroy () {
    window.addEventListener('resize', this.calcContentHeight)
  }
}
</script>

<style lang="scss" scoped>
.autocomplete__wrapper {
  height: 74px;
}

.form__footer {
  align-items: center;
  background-color: #fff;
  border-top: 1px solid $gray-4;
  height: 83px;
  display: flex;
  justify-content: center;
  margin-top: 1px;
}

.fields--disabled {
  position: relative;

  &:before {
    background-color: rgba(#fff, 0.5);
    bottom: 0;
    content: '';
    cursor: not-allowed;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 1;
  }
}
</style>
